import React, { useEffect, useState } from "react";
import { Eye, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { collection, collectionGroup, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import UploadModal from "../../components/UploadModal";
import DetailModalContent from "../../components/DetailModalContent";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { DataTable, DataTableColumn } from "../../components/ui/data-table";

const ManageData: React.FC = () => {
  const [affiliations, setAffiliations] = useState<string[]>([]);
  const [uploads, setUploads] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [selectedAff, setSelectedAff] = useState<string>("전체");
  const [selectedYear, setSelectedYear] = useState<string>("전체");
  const [selectedMonth, setSelectedMonth] = useState<string>("전체");
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalData, setModalData] = useState<any>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchAffiliations = async (): Promise<string[]> => {
    try {
      const snap = await getDocs(collection(db, "users"));
      const raw = snap.docs.map((doc) => doc.data().affiliation);

      const affiliations = Array.from(new Set(raw)).filter(
        (name) => name && name !== "총무노트" && name !== "버디"
      );

      return affiliations;
    } catch (error) {
      console.error("Affiliation fetch failed:", error);
      return [];
    }
  };

  const fetchUploads = async () => {
    const snap = await getDocs(collectionGroup(db, "salesUploads"));
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setUploads(data);
  };

  useEffect(() => {
    fetchAffiliations().then(setAffiliations);
    fetchUploads();
  }, []);

  useEffect(() => {
    const filtered = uploads
      .filter((item) => {
        const affMatch =
          selectedAff === "전체" || item.affiliation === selectedAff;
        const yearMatch =
          selectedYear === "전체" || item.year === Number(selectedYear);
        const monthMatch =
          selectedMonth === "전체" || item.month === Number(selectedMonth);
        return affMatch && yearMatch && monthMatch;
      })
      .map((item) => ({
        ...item,
        id: item.id,
      }));
    setFiltered(filtered);
  }, [uploads, selectedAff, selectedYear, selectedMonth]);

  const handleDelete = async (record: any) => {
    if (!record?.id) {
      return toast.error("삭제할 수 없습니다. ID 또는 경로 없음");
    }

    try {
      setDeleting(true);
      await deleteDoc(
        doc(db, `affiliations/${record.affiliation}/salesUploads/${record.id}`)
      );
      toast.success("삭제 완료");
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchUploads();
    } catch (err) {
      console.error(err);
      toast.error("삭제 중 오류 발생");
    } finally {
      setDeleting(false);
    }
  };

  const columns: DataTableColumn<any>[] = [
    {
      title: "번호",
      render: (_: any, __: any, index: number) => index + 1,
      width: 70,
      align: "center",
    },
    { title: "제목", dataIndex: "title", sortable: true, sortKey: "title" },
    {
      title: "고객사",
      dataIndex: "affiliation",
      sortable: true,
      sortKey: "affiliation",
    },
    { title: "연도", dataIndex: "year", sortable: true, sortKey: "year" },
    { title: "월", dataIndex: "month", sortable: true, sortKey: "month" },
    {
      title: "상세보기/삭제",
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              setModalData(record);
              setIsModalVisible(true);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="destructive"
            size="icon"
            onClick={() => {
              setDeleteTarget(record);
              setIsDeleteOpen(true);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-extrabold">
          데이터 관리{" "}
          <span className="text-sm font-normal text-muted-foreground">
            월 별 데이터를 업로드하세요.
          </span>
        </h1>
      </div>

      <div className="mt-4">
        <DataTable
          dataSource={filtered}
          columns={columns}
          pagination={false}
          search={{
            keys: ["title", "affiliation"],
            placeholder: "제목/고객사 검색",
          }}
          toolbarRight={
            <div className="flex flex-wrap gap-2">
              <Select value={selectedAff} onValueChange={setSelectedAff}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="고객사 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="전체">전체</SelectItem>
                  {affiliations.map((aff) => (
                    <SelectItem key={aff} value={aff}>
                      {aff}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue placeholder="연도" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="전체">전체</SelectItem>
                  {[2024, 2025, 2026].map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[90px]">
                  <SelectValue placeholder="월" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="전체">전체</SelectItem>
                  {[...Array(12)].map((_, i) => {
                    const m = i + 1;
                    return (
                      <SelectItem key={m} value={String(m)}>
                        {m}월
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button onClick={() => setIsUploadModalOpen(true)}>
                <UploadCloud className="mr-2 h-4 w-4" />
                엑셀 업로드
              </Button>
              <UploadModal
                open={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSuccess={fetchUploads}
                onUploadComplete={() => {}}
                affiliations={affiliations}
              />
            </div>
          }
        />
      </div>

      <Dialog open={isModalVisible} onOpenChange={setIsModalVisible}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1600px]">
          <DialogHeader>
            <DialogTitle>상세 정보</DialogTitle>
          </DialogHeader>
          <div className="max-h-[90vh] overflow-auto">
            <DetailModalContent modalData={modalData} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>정말 삭제하시겠습니까?</DialogTitle>
          </DialogHeader>
          {deleteTarget ? (
            <div className="space-y-2 text-sm">
              <div>
                고객사: <b>{deleteTarget.affiliation}</b>
              </div>
              <div>
                연월:{" "}
                <b>
                  {deleteTarget.year}-
                  {String(deleteTarget.month).padStart(2, "0")}
                </b>
              </div>
              <div>
                제목: <b>{deleteTarget.title}</b>
              </div>
              <div className="text-red-600">
                삭제 후 되돌릴 수 없습니다.
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
              disabled={deleting}
            >
              {deleting ? "삭제 중..." : "삭제"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageData;
